import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import LearningDetailCard from '@/components/organisms/Card/LearningDetailCard';
import { studentHomeworkApi } from '@/services/api';

interface UnitProgress {
    id: number;
    code: string;
    title: string;
    unitType: string;
    unitCategory: string;
    orderIndex: number;
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
    progress: number;
    score?: number;
    lastAccessedDate?: string;
    textbookId?: number;
    textbookTitle?: string;
    textbookName?: string;
}

interface HomeworkDetail {
    id: number;
    title: string;
    startDate: string;
    endDate: string;
    status: 'completed' | 'in-progress' | 'waiting';
    overallProgress: number;
    totalUnits: number;
    completedUnits: number;
    maxUnitsPerDay?: number;
    dailyLimit?: any;
    scoringRequired?: boolean;
    units: UnitProgress[];
}

// 숙제 상세
export default function StudentHomeworkDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const filterTextbookId = Number(searchParams.get('textbookId') || 0);
    const filterTextbookTitle = (searchParams.get('textbookTitle') || searchParams.get('textbookName') || '').trim();
    const slugPrefix = getSlugPrefix(pathname);
    const [homeworkDetail, setHomeworkDetail] = useState<HomeworkDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // 숙제 상세 정보 조회
    const fetchHomeworkDetail = async () => {
        if (!id) return;
        
        setIsLoading(true);
        setError('');
        
        try {
            console.log('=== 숙제 상세 API 호출 시작 ===');
            console.log('Schedule ID:', id);
            const response = await studentHomeworkApi.detail(Number(id));
            console.log('=== API 응답 데이터 ===');
            console.log('전체 응답:', response);
            console.log('성공 여부:', response.success);
            console.log('응답 데이터:', response.data);
            
            if (response.success) {
                const raw = response.data || {};
                const textbooks = Array.isArray(raw.textbooks) ? raw.textbooks : [];
                const flatUnits: UnitProgress[] = [];
                textbooks.forEach((tb: any) => {
                    const tbId = Number(tb?.textbookId ?? tb?.id ?? 0);
                    const tbTitle = String(tb?.textbookTitle ?? tb?.title ?? '');
                    const units = Array.isArray(tb?.units) ? tb.units : [];
                    units.forEach((u: any, idx: number) => {
                        flatUnits.push({
                            id: Number(u.unitId ?? u.id ?? idx),
                            code: String(u.unitCode ?? ''),
                            title: String(u.unitTitle ?? ''),
                            unitType: String(u.unitType ?? ''),
                            unitCategory: String(u.unitCategory ?? ''),
                            orderIndex: Number(u.orderIndex ?? idx),
                            status: String(u.status ?? 'NOT_STARTED') as UnitProgress['status'],
                            progress: Number(u.progress ?? 0),
                            score: u.score != null ? Number(u.score) : undefined,
                            textbookId: tbId || undefined,
                            textbookTitle: tbTitle || undefined,
                        });
                    });
                });

                const filteredUnits =
                    filterTextbookId > 0
                        ? flatUnits.filter(u => (u.textbookId ?? -1) === filterTextbookId)
                        : (filterTextbookTitle
                            ? flatUnits.filter(u => {
                                const a = (u.textbookTitle || u.textbookName || '').toLowerCase();
                                return a === filterTextbookTitle.toLowerCase();
                              })
                            : flatUnits);

                const mapped: HomeworkDetail = {
                    id: Number(raw.scheduleId ?? id),
                    title: String(raw.scheduleTitle ?? ''),
                    startDate: String(raw.startDate ?? ''),
                    endDate: String(raw.endDate ?? ''),
                    status: String(raw.status ?? 'waiting') as HomeworkDetail['status'],
                    overallProgress: Number(raw.progress ?? raw.overallProgress ?? 0),
                    totalUnits: Number(raw.totalUnits ?? filteredUnits.length ?? 0),
                    completedUnits: Number(raw.completedUnits ?? 0),
                    maxUnitsPerDay: raw.maxUnitsPerDay != null ? Number(raw.maxUnitsPerDay) : undefined,
                    dailyLimit: raw.dailyLimit,
                    scoringRequired: Boolean(raw.isHomework),
                    units: filteredUnits,
                };

                console.log('유닛 목록(매핑 후):', mapped.units);
                console.log('유닛 개수(매핑 후):', mapped.units.length);
                setHomeworkDetail(mapped);
            } else {
                console.error('API 실패 메시지:', response.message);
                setError(response.message || '숙제 정보를 불러오는데 실패했습니다.');
            }
        } catch (err: any) {
            console.error('=== 숙제 상세 조회 에러 ===');
            console.error('에러 객체:', err);
            console.error('에러 응답:', err?.response);
            console.error('에러 데이터:', err?.response?.data);
            const errorMessage = err?.response?.data?.message || '숙제 정보를 불러오는 중 오류가 발생했습니다.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchHomeworkDetail();
    }, [id, filterTextbookId]);

    if (isLoading) {
        return (
            <main className='flex flex-1 justify-center items-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
                <div className='py-10 text-center text-gray-500'>
                    숙제 정보를 불러오는 중...
                </div>
            </main>
        );
    }

    if (error || !homeworkDetail) {
        return (
            <main className='flex flex-1 justify-center items-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
                <div className='py-10 text-center'>
                    <p className='mb-4 text-red-500'>{error || '숙제를 찾을 수 없습니다.'}</p>
                    <button 
                        onClick={() => navigate(`${slugPrefix}/homework/list`)}
                        className='text-blue-500 hover:underline'
                    >
                        목록으로 돌아가기
                    </button>
                </div>
            </main>
        );
    }

return (    
    <main className='flex flex-1 justify-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
        <div className='flex flex-col w-full'>
            <div className='px-5 pt-6 pb-3 lg:pt-0'>
                <h1 className='heading1'>{homeworkDetail.title}</h1>
                <p className='mt-1 text-sm text-gray-600'>
                    {/* {homeworkDetail.startDate} ~ {homeworkDetail.endDate} */}
                </p>
            </div>

            <div className='flex flex-col gap-4 px-5 pb-3'>
                <LearningDetailCard 
                    status={homeworkDetail.status} 
                    progress={homeworkDetail.overallProgress} 
                    type='homework'
                    units={homeworkDetail.units}
                    scheduleId={homeworkDetail.id}
                    dailyLimit={homeworkDetail.dailyLimit}
                />
            </div>
        </div>
    </main>
  )
}